import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  sharePlatforms,
  referralTypes,
  mostSharedEntities,
  topReferrers,
  shareDashboardStats,
  referralScoreActions,
  referralRewards,
  formatShareCount,
  formatConversionRate,
  type MostSharedEntity,
  type TopReferrer,
  type SharePlatform,
} from '@/mocks/rfwShares';

function SharePlatformBadge({ platform }: { platform: SharePlatform }) {
  const config = sharePlatforms.find((p) => p.id === platform);
  if (!config) return null;
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.bgColor}`}>
      <i className={`${config.icon} text-xs ${config.color}`}></i>
      <span className={`text-[10px] font-semibold ${config.color}`}>{config.label}</span>
    </div>
  );
}

function SharedEntityCard({ entity }: { entity: MostSharedEntity }) {
  return (
    <Link
      to={entity.linkUrl}
      className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group"
    >
      <div className="relative">
        <img src={entity.imageUrl} alt={entity.name} className="w-full h-36 object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-800 text-[9px] font-semibold border border-background-200/50 backdrop-blur-sm">
          {entity.type}
        </span>
      </div>
      <div className="p-4">
        <p className="text-sm font-bold text-foreground-900 leading-snug mb-0.5 line-clamp-1">{entity.name}</p>
        <p className="text-[11px] text-foreground-500 mb-3 line-clamp-1">{entity.subtitle}</p>

        <div className="flex items-center justify-between text-[11px] mb-3">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-foreground-500">
              <i className="ri-share-forward-line text-[10px]"></i>
              {formatShareCount(entity.totalShares)}
            </span>
            <span className="flex items-center gap-1 text-emerald-600">
              <i className="ri-arrow-up-line text-[10px]"></i>
              +{formatShareCount(entity.weeklyShares)} sem.
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mb-3">
          <SharePlatformBadge platform={entity.topPlatform} />
          <span className="text-[10px] text-foreground-400">#{entity.type.toLowerCase()}</span>
        </div>

        <div className="flex items-center gap-4 text-[11px]">
          {(['whatsapp', 'facebook', 'x', 'telegram'] as SharePlatform[]).map((p) => (
            <div key={p} className="flex items-center gap-1 text-foreground-500">
              <i className={`${sharePlatforms.find((sp) => sp.id === p)?.icon} text-[10px]`}></i>
              <span>{formatShareCount(entity.platformsBreakdown[p])}</span>
            </div>
          ))}
        </div>
      </div>
    </Link>
  );
}

function ReferrerCard({ referrer }: { referrer: TopReferrer }) {
  const positionColors: Record<number, string> = {
    1: 'bg-amber-100 text-amber-700 border-amber-300',
    2: 'bg-foreground-100 text-foreground-600 border-foreground-300',
    3: 'bg-amber-50 text-amber-600 border-amber-200',
  };

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 group">
      <div className="relative shrink-0">
        <div className={`absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full border-2 border-background-50 flex items-center justify-center text-[9px] font-extrabold ${positionColors[referrer.position] || 'bg-background-100 text-foreground-500 border-background-200'}`}>
          {referrer.position}
        </div>
        <img src={referrer.avatar} alt={referrer.name} className="w-11 h-11 rounded-full object-cover ring-2 ring-background-200" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-sm font-bold text-foreground-900 leading-snug">{referrer.name}</p>
          <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold ${
            referrer.tier === 'Leyenda RFW' ? 'bg-accent-100 text-accent-700' :
            referrer.tier === 'Embajador RFW' ? 'bg-violet-100 text-violet-700' :
            'bg-primary-100 text-primary-700'
          }`}>
            {referrer.tier}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-foreground-500">
            <span className="font-semibold text-foreground-900">{referrer.acceptedReferrals}</span>/{referrer.totalReferrals} referidos
          </span>
          <span className="flex items-center gap-1 text-emerald-600">
            <i className="ri-check-double-line text-[9px]"></i>
            {formatConversionRate(referrer.conversionRate)}
          </span>
          <span className="flex items-center gap-1 text-primary-600 font-semibold">
            <i className="ri-flashlight-line text-[9px]"></i>
            {formatShareCount(referrer.scoreEarned)} pts
          </span>
        </div>
        {referrer.badges.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {referrer.badges.slice(0, 2).map((b) => (
              <span key={b} className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[8px] font-medium">{b}</span>
            ))}
            {referrer.badges.length > 2 && (
              <span className="text-[8px] text-foreground-400">+{referrer.badges.length - 2}</span>
            )}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="text-lg font-extrabold text-foreground-950">{formatShareCount(referrer.scoreEarned)}</p>
        <p className="text-[9px] text-foreground-500">pts RFW</p>
      </div>
    </div>
  );
}

export default function CompartidosSection() {
  const [activeTab, setActiveTab] = useState<'mostShared' | 'topReferrers'>('mostShared');

  const shared = mostSharedEntities.slice(0, 6);
  const referrers = topReferrers.slice(0, 6);

  const tabs = [
    { id: 'mostShared' as const, label: 'Lo Más Compartido', icon: 'ri-share-forward-line', count: shared.length },
    { id: 'topReferrers' as const, label: 'Top Referidores', icon: 'ri-user-add-line', count: referrers.length },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
            SISTEMA DE COMPARTIDOS Y REFERIDOS RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Convertí Usuarios en <span className="text-primary-600">Promotores</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Compartí contenido en 6 plataformas. Invitá amigos, oyentes, radios y locutores. Cada referido exitoso suma Score, desbloquea recompensas y te acerca al VIP.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Compartidos Totales', value: formatShareCount(shareDashboardStats.totalShares), icon: 'ri-share-forward-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Invitaciones Enviadas', value: formatShareCount(shareDashboardStats.totalInvitations), icon: 'ri-send-plane-line', color: 'text-accent-600 bg-accent-50' },
            { label: 'Aceptadas', value: formatShareCount(shareDashboardStats.acceptedInvitations), icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Tasa de Conversión', value: `${shareDashboardStats.conversionRate}%`, icon: 'ri-line-chart-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Score Generado', value: formatShareCount(shareDashboardStats.totalScoreGenerated), icon: 'ri-flashlight-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Share Platforms Strip */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-10">
          {sharePlatforms.map((platform) => (
            <div key={platform.id} className={`flex flex-col items-center gap-2 p-4 rounded-xl ${platform.bgColor} border border-background-200/70 hover:border-primary-200/60 transition-all duration-200`}>
              <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center">
                <i className={`${platform.icon} text-lg ${platform.color}`}></i>
              </div>
              <p className="text-[10px] font-semibold text-foreground-800">{platform.label}</p>
              <span className="text-[9px] text-foreground-500">{platform.id === 'whatsapp' ? '48.4%' : platform.id === 'facebook' ? '26.2%' : platform.id === 'telegram' ? '10.8%' : platform.id === 'x' ? '7.3%' : platform.id === 'qr' ? '3.9%' : '3.4%'}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm cursor-pointer'
                    : 'text-foreground-600 hover:text-foreground-900 cursor-pointer'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {activeTab === 'mostShared' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shared.map((entity) => (
              <SharedEntityCard key={entity.id} entity={entity} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {referrers.map((referrer) => (
              <ReferrerCard key={referrer.position} referrer={referrer} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/compartidos"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-share-forward-line"></i>
            Ir al Hub de Compartidos y Referidos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-share-forward-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Compartir debe existir en toda la plataforma.
          </p>
          <p className="text-xs text-foreground-600 max-w-xl mx-auto">
            Cada contenido de RFW puede y debe compartirse. Cada referido que traés a la plataforma te acerca a recompensas, insignias, beneficios y status VIP. Compartir no es solo difundir — es construir comunidad.
          </p>
        </div>
      </div>
    </section>
  );
}